import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sales Verification tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function SalesTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="sales-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">
                    Sales Verification
                  </h2>

                  {/* New: multi-level listing sales pending Directory verification */}
                  <div className="wm-card p-6 rounded-lg border border-gold/30">
                    <h3 className="text-lg font-semibold text-gold mb-1 flex items-center gap-2">
                      Pending Listing Sales <span className="text-xs text-muted-foreground">(multi-level commission)</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      Sales recorded by members. Commissions credit only after the Directory transaction is matched
                      (auto), or you manually verify here. Founder=50/50, Growth=25/25/50, Ascend=10/15/25/50.
                    </p>
                    {pendingListingSales.length > 0 ? (
                      <div className="space-y-3">
                        {pendingListingSales.map((s) => (
                          <div key={s.id} className="border border-border rounded-md p-4" data-testid={`listing-sale-${s.id}`}>
                            <div className="flex justify-between items-start gap-4 flex-wrap">
                              <div className="flex-1 min-w-[260px]">
                                <p className="text-xl font-bold text-primary">${(s.sale_amount || 0).toLocaleString()}</p>
                                <p className="text-foreground text-sm">
                                  {s.seller_name || s.seller_email || s.seller_id} · <span className="capitalize text-gold">{s.seller_tier || '—'}</span>
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Submitted: {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                                  {s.edirectory_ref && <> · eDir ref: {s.edirectory_ref}</>}
                                </p>
                                {s.commission_recipients?.length > 0 && (
                                  <div className="mt-3 grid sm:grid-cols-2 gap-1 text-xs">
                                    {s.commission_recipients.map((r, idx) => (
                                      <div key={idx} className="flex justify-between border border-border/50 rounded px-2 py-1">
                                        <span className="capitalize">
                                          {r.role.replace('_', ' ')} ({r.percent}%)
                                        </span>
                                        <span className="font-mono text-gold">${(r.amount || 0).toFixed(2)}</span>
                                      </div>
                                    ))}
                                    <div className="flex justify-between border border-border/50 rounded px-2 py-1 bg-muted/20">
                                      <span>Platform</span>
                                      <span className="font-mono">${(s.platform_commission || 0).toFixed(2)}</span>
                                    </div>
                                  </div>
                                )}
                                {(!s.commission_recipients || s.commission_recipients.length === 0) && (
                                  <p className="text-xs text-amber-500 mt-2">
                                    ⚠ Legacy row (pre-multi-level engine). Verifying will only flip flags; no balances change.
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleVerifyListingSale(s.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`verify-listing-sale-${s.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" /> Verify & Credit
                                </Button>
                                {s.credited && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleUnverifyListingSale(s.id)}
                                    data-testid={`unverify-listing-sale-${s.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" /> Reverse
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                                  onClick={() => handleCancelListingSale(s.id)}
                                  data-testid={`cancel-listing-sale-${s.id}`}
                                  title="Mark customer as cancelled — reverses all related commissions."
                                >
                                  <Ban className="w-4 h-4 mr-1" /> Cancel
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No listing sales pending verification.</p>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-foreground mt-8 mb-2">
                    Service Sales ({pendingSales.length} pending)
                  </h3>
                  {pendingSales.length > 0 ? (
                    <div className="space-y-4">
                      {pendingSales.map((sale) => (
                        <div key={sale.id} className="wm-card p-6 rounded-lg border border-primary/20" data-testid="pending-sale-item">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-2xl font-bold text-primary mb-1">${sale.amount.toLocaleString()}</p>
                              <p className="text-foreground">{sale.description}</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                User: {sale.user_id} | Submitted: {new Date(sale.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleVerifySale(sale.id, true)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid="approve-sale-btn"
                              >
                                <Check className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button
                                onClick={() => handleVerifySale(sale.id, false)}
                                variant="destructive"
                                data-testid="reject-sale-btn"
                              >
                                <X className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="wm-card p-8 rounded-lg text-center">
                      <p className="text-muted-foreground">No pending service sales</p>
                    </div>
                  )}
    </div>
  );
}
